<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { Checkbox, ColoredButton } from '@/shared/ui';
  import { useMatrixFillLabContext, type LabRule } from '../../model/useMatrixFillLab';
  import FieldRef from './FieldRef.vue';

  const lab = useMatrixFillLabContext();

  const ACTION_LABEL: Record<LabRule['action'], string> = {
    show: 'mostrar',
    hide: 'ocultar',
    disable: 'desabilitar',
  };

  const SELECT_CLASS =
    'w-full rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] px-2 py-1.5 text-xs text-[var(--color-gray-07)] disabled:opacity-50';

  const triggerFieldId = ref('');
  const triggerOption = ref('');
  const action = ref<LabRule['action']>('hide');
  const targetFieldIds = ref<string[]>([]);

  const triggerFields = computed(() => lab.fieldOptions.value.filter((field) => field.isTrigger));
  const triggerOptions = computed(
    () => lab.fieldOptions.value.find((field) => field.id === triggerFieldId.value)?.options ?? [],
  );
  const targetCandidates = computed(() => lab.fieldOptions.value.filter((field) => field.id !== triggerFieldId.value));
  const canAdd = computed(() => triggerFieldId.value !== '' && triggerOption.value !== '' && targetFieldIds.value.length > 0);

  // The option belongs to the trigger field, so it is dropped together with it; the targets
  // only lose the field that just became the trigger.
  watch(triggerFieldId, (id) => {
    triggerOption.value = '';
    targetFieldIds.value = targetFieldIds.value.filter((target) => target !== id);
  });

  watch(() => lab.matrix.value?.id, () => {
    triggerFieldId.value = '';
    targetFieldIds.value = [];
  });

  function add(): void {
    if (!canAdd.value) return;
    lab.addRule({
      triggerFieldId: triggerFieldId.value,
      triggerOption: triggerOption.value,
      action: action.value,
      targetFieldIds: [...targetFieldIds.value],
    });
    targetFieldIds.value = [];
  }

  function toggleTarget(id: string): void {
    targetFieldIds.value = targetFieldIds.value.includes(id)
      ? targetFieldIds.value.filter((item) => item !== id)
      : [...targetFieldIds.value, id];
  }
</script>

<template>
  <div
    class="flex flex-col gap-2"
    data-testid="rule-editor"
  >
    <p
      v-if="triggerFields.length === 0"
      class="italic text-[var(--color-gray-05)]"
    >
      Esta matriz não tem campo com opções (dropdown ou checkbox), então nenhuma regra pode ser criada.
    </p>
    <template v-else>
      <label class="flex flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Quando o campo</span>
        <select
          v-model="triggerFieldId"
          :class="SELECT_CLASS"
          data-testid="rule-trigger"
        >
          <option value="">
            Escolha o gatilho
          </option>
          <option
            v-for="field in triggerFields"
            :key="field.id"
            :value="field.id"
          >
            {{ field.label }} ({{ field.typeLabel }})
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Tiver a opção</span>
        <select
          v-model="triggerOption"
          :class="SELECT_CLASS"
          :disabled="!triggerFieldId"
          data-testid="rule-option"
        >
          <option value="">
            Escolha a opção
          </option>
          <option
            v-for="option in triggerOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Então</span>
        <select
          v-model="action"
          :class="SELECT_CLASS"
          data-testid="rule-action"
        >
          <option
            v-for="(label, value) in ACTION_LABEL"
            :key="value"
            :value="value"
          >
            {{ label }}
          </option>
        </select>
      </label>
      <div class="flex flex-col gap-1">
        <span class="text-[10px] font-semibold uppercase text-[var(--color-gray-05)]">Os campos ({{ targetFieldIds.length }})</span>
        <div class="max-h-36 overflow-auto rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] p-1.5">
          <label
            v-for="field in targetCandidates"
            :key="field.id"
            class="flex cursor-pointer items-center gap-2 py-0.5"
          >
            <Checkbox
              :id="`rule-target-${field.id}`"
              :checked="targetFieldIds.includes(field.id)"
              @toggle="toggleTarget(field.id)"
            />
            <FieldRef
              :id="field.id"
              :label="field.label"
              :locate="false"
            />
          </label>
        </div>
      </div>
      <ColoredButton
        text="Adicionar regra"
        :disabled="!canAdd"
        class="self-start"
        data-testid="rule-add"
        @click="add"
      />
    </template>

    <ul
      v-if="lab.labRules.value.length > 0"
      class="flex flex-col gap-1.5"
      data-testid="rule-editor-list"
    >
      <li
        v-for="rule in lab.labRules.value"
        :key="rule.id"
        class="rounded-md border border-[var(--color-gray-02)] bg-[var(--color-white-01)] p-2"
      >
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{ rule.name }} (simulada)</span>
          <button
            type="button"
            class="ml-auto text-[10px] text-red-600 underline"
            @click="lab.removeRule(rule.id)"
          >
            remover
          </button>
        </div>
        <p class="mt-1 flex flex-wrap items-baseline gap-x-1">
          Quando
          <FieldRef :id="rule.triggerFieldId" />
          = "{{ rule.triggerOption }}", {{ ACTION_LABEL[rule.action] }}:
          <FieldRef
            v-for="target in rule.targetFieldIds"
            :id="target"
            :key="target"
          />
        </p>
      </li>
    </ul>
  </div>
</template>
